import { createRouter, createWebHistory } from 'vue-router'

import { useMainStore } from '@/store/main'
import { useLocationStore } from '@/store/location'
import { useStatusStore } from '@/store/status'
import { useFeatureFlagsStore } from '@/store/featureFlags'
import { loadLanguageAsync } from '@/i18n'
import ScreenLayout from '@/components/layouts/ScreenLayout'

import { funnelRoutes, entryPointRoutes } from '@/funnels/funnelUtils.ts'
import { DOMAINS_FROM_CONFIG } from '@/funnels/funnels.ts'
import paywallRoutes from '@/modules/Paywall/paywallRoutes.ts'
import telegraRoutes from '@/modules/Telegra/telegraRoutes.ts'
import shippingDetailsRoutes from '@/modules/ShippingDetails/shippingDetailsRoutes.ts'
import intakeFormRoutes from '@/modules/IntakeForm/intakeFormRoutes.ts'

// basePath is resolved lazily in the router's history option
// since the location store needs pinia to be installed first
let _basePath = null
function getBasePath() {
	if (_basePath === null) {
		const locationStore = useLocationStore()
		_basePath = locationStore.basePath
	}
	return _basePath
}

const routes = [
	{
		path: '/',
		component: ScreenLayout,
		children: [
			// Точки входа в воронку (сейчас одна: `/app`). Каждая рендерит лендинг;
			// area резолвится из префикса пути.
			// Основная точка входа держит имя `landing` (fallback-навигации).
			...entryPointRoutes(),

			// Голый base `/survey/` → 404: точка входа теперь явная (`/app`),
			// корень больше не лендинг. SPA-soft-404 (как и catch-all ниже).
			{
				path: '',
				component: () => import(/* webpackChunkName: "not-found" */ '@/views/EmptyPage.vue'),
				meta: {
					hideHeader: true,
				},
			},

			{
				path: 'golden-ticket',
				name: 'golden-ticket',
				component: () => import(/* webpackChunkName: "golden-ticket" */ '@/views/GoldenTicket.vue'),
				meta: {
					hideHeader: true,
					skipExperiments: true,
				},
			},

			{
				path: 'redirect',
				name: 'redirect',
				component: () => import(/* webpackChunkName: "redirect" */ '@/views/Redirect.vue'),
				meta: {
					requiresUser: true,
					skipExperiments: true,
				},
			},

			{
				path: 'prescription-review',
				name: 'prescription-review',
				component: () => import(/* webpackChunkName: "prescription-review" */ '@/views/PrescriptionReview.vue'),
				meta: {
					hideHeader: true,
					skipExperiments: true,
				},
			},

			// DEV-34805: the payment-confirmation screen was replaced by prescription-review
			// (account creation moved after the health intake). Covers mid-flow reloads of
			// sessions that paid on a pre-cutover deploy; query preserved by vue-router.
			{ path: 'payment-confirmation', redirect: '/prescription-review' },

			// DEV-35018: Auth0 is gone from the funnel entirely — `/signin` (Universal Login →
			// v2 signup → login → /success) and `/success` no longer exist. The whole funnel now
			// ends on `intake-done` for every cohort, renewals included, and the account is
			// created server-side (v3 signup); the patient signs in by email OTP in the app.
			// These two redirects cover mid-flow reloads across the cutover deploy and
			// neutralise any stale `to=signin` CRM link (which used to resolve to a real
			// /signin and could finish the funnel WITHOUT the intake — see
			// docs/crm-email-links.md).
			{ path: 'signin', redirect: '/intake-done' },

			// DEV-35018: the welcome interstitial between prescription-review and the intake is
			// gone — prescription-review's CTA now hands off straight to /intake-form. Covers
			// mid-flow reloads of sessions that reached the welcome screen on a pre-cutover
			// deploy; they already have the Telegra order (its CTA ensured it), so /intake-form
			// is exactly where the removed CTA would have taken them. Never a CRM target
			// (re-activation uses to=prescription-review, docs/crm-email-links.md).
			{ path: 'intake-welcome', redirect: '/intake-form' },

			// Терминальный экран ВСЕЙ воронки — интейк завершён + скачивание аппа. Аккаунт
			// создаёт бек (v3 signup), логин по email-OTP происходит уже в аппе. С DEV-35018
			// сюда приходят все, включая реньювал/рефилл: Auth0-хвоста больше нет
			// (modules/intakeFinish.ts). Исключение — вебвью аппа: там интейк отдаёт
			// управление деплинком, роут не меняется (DEV-35414).
			{
				path: 'intake-done',
				name: 'intake-done',
				component: () => import(/* webpackChunkName: "intake-done" */ '@/views/IntakeDone.vue'),
				meta: {
					hideHeader: true,
					skipExperiments: true,
				},
			},
		],
	},

	// glp-wb удалена — исторический PATH-трафик (/wb, /glp-wb*) уходит на glp-app
	// (product sign-off:
	// https://simple-app-hq.slack.com/archives/C0BBQK4UYEL/p1784621200637709,
	// план: docs/glp-wb-removal-plan.md). CRM-ретаргетинг по ИМЕНАМ роутов
	// plan-kova-glp-1-* мапится на glp-app-пейволы алиасами в Redirect.vue
	// (wb-пейвол-стаб снят в DEV-35540). Редиректы строкой: vue-router сам
	// переносит query/hash через string-редирект (handleRedirectRecord), а
	// ручное доследование редиректов в App.vue.created() понимает только
	// string/object-формы. Mid-funnel диплинки намеренно рестартят воронку:
	// наборы экранов wb/app-опросников различались, помэппить страницы нельзя.
	{ path: '/wb', redirect: '/app' },
	{ path: '/glp-wb', redirect: '/glp-app' },
	{ path: '/glp-wb/ob/:page(.*)?', redirect: '/glp-app' },

	...paywallRoutes,
	// /shipping-details — адрес доставки после оплаты в чекауте `/plan/*` (DEV-36112).
	...shippingDetailsRoutes,
	// /telegra — живой интейк Telegra, оставлен как fallback: боевой путь ведёт на
	// /intake-form (интейк из билдера), см. PrescriptionReview.
	...telegraRoutes,
	...intakeFormRoutes,

	// DEV-35018: `/success` was the terminal screen of the Auth0 tail (reachable only from
	// /signin) — removed with it. `intake-done` carries the same app-download affordance.
	{ path: '/success', redirect: '/intake-done' },

	{
		path: '/:pathMatch(.*)*',
		name: '404',
		component: () => import(/* webpackChunkName: "not-found" */ '@/views/EmptyPage.vue'),
	},
]

let _router = null
function getRouter() {
	if (!_router) {
		const hostname = useLocationStore().hostname
		const currentDomainConfig = DOMAINS_FROM_CONFIG.find(({ regex }) => regex.test(hostname))?.domainConfig

		_router = createRouter({
			history: createWebHistory(getBasePath()),
			routes: [...routes, ...funnelRoutes(currentDomainConfig?.pathnames)],
			scrollBehavior(to, from) {
				// Открытие/закрытие модалки не должно скроллить страницу
				if (to.path === from.path && (Boolean(to.query.popup) || Boolean(from.query.popup))) {
					return null
				}

				return new Promise((resolve) => {
					setTimeout(() => {
						resolve({ top: 0, left: 0 })
					}, 0)
				})
			},
		})

		_router.beforeEach(async (to) => {
			const mainStore = useMainStore()
			const statusStore = useStatusStore()

			// Routes outside the experiment surface (redirect, prescription-review, intake-done,
			// telegra, intake-form) must not wait for the AB-platform context — open its render
			// gate right away. The module itself opens it on ready/failure/deadline for every
			// other route.
			if (to.meta.skipExperiments) {
				statusStore.setResourceReadiness('abPlatform')
			}

			const blockingRequests = [
				mainStore.fetchGeo({
					country: to.query.country,
					region: to.query.region,
				}),
				loadLanguageAsync(),
				// Remote feature flags. Self-contained: hydrate() is fail-open and flips
				// its own 'config' readiness in a finally, so a flag-service outage can't
				// hang the startup splash.
				useFeatureFlagsStore().hydrate(),
			]

			await Promise.all(blockingRequests)
			statusStore.setResourceReadiness('network')

			let isObPreview = mainStore.isPreviewMode

			if (isObPreview) {
				if (!mainStore.user) {
					await mainStore.setupPreviewUser()
				}
				return true
			}

			return true
		})
	}
	return _router
}

export default getRouter
